import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Button,
  IconButton,
  Avatar,
  Chip,
  Typography,
  Dialog,
  DialogContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Download,
  Share,
  QrCodeScanner,
  FlashOn,
  Close,
  Person,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import GradientSurface from '../../components/shared/GradientSurface';
import BottomSheet from '../../components/shared/BottomSheet';
import { RADIUS, SHADOWS_LIGHT, SHADOWS_DARK } from '../../theme/tokens';
import { easing, duration as dur } from '../../theme/motion';
import { useReducedMotionPreferred } from '../../hooks/useReducedMotionPreferred';
import DigitalCardFront from './qrPass/DigitalCardFront';
import DigitalCardBack from './qrPass/DigitalCardBack';

const MotionBox = motion(Box);

// Default fallback ads when API returns nothing
const DEFAULT_CARD_ADS = [
  { id: 1, title: 'Partner Offers', offer_text: 'Save 20%', bg_color: null, text_color: null, offer_color: null },
  { id: 2, title: 'Coaching', offer_text: 'Free Demo', bg_color: null, text_color: null, offer_color: null },
];

/**
 * StudentQRPass — Digital Library Card with flip animation.
 * Fullscreen pass uses BottomSheet on mobile, Dialog on desktop.
 */
function StudentQRPass({ showSnackbar }) {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const reducedMotion = useReducedMotionPreferred();
  const isLight = theme.palette.mode === 'light';

  const [showFullscreen, setShowFullscreen] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [qrLoaded, setQrLoaded] = useState(false);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [cardAds, setCardAds] = useState([]);
  const [, setLoading] = useState(true);
  const [viewsTracked, setViewsTracked] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
  const qrUrl = user?.qr_url ? `${API_BASE}${user.qr_url}` : null;

  useEffect(() => {
    if (qrUrl) {
      const img = new Image();
      img.onload = () => setQrLoaded(true);
      img.src = qrUrl;
    }
  }, [qrUrl]);

  useEffect(() => {
    fetchSubscription();
    fetchCardAds();
  }, []);

  const fetchSubscription = async () => {
    try {
      const res = await api.get('/subscriptions/active');
      if (res.data.data.subscription) {
        setActiveSubscription(res.data.data.subscription);
      }
    } catch {
      // no active subscription — not an error state
    } finally {
      setLoading(false);
    }
  };

  const fetchCardAds = async () => {
    try {
      const params = new URLSearchParams();
      if (user?.city) params.append('userCity', user.city);
      const qs = params.toString();
      const res = await api.get(`/card-ads/active${qs ? `?${qs}` : ''}`);
      const ads = res.data.data.ads;
      setCardAds(ads && ads.length > 0 ? ads : DEFAULT_CARD_ADS);
    } catch {
      setCardAds(DEFAULT_CARD_ADS);
    }
  };

  // Track card ad views when back of card is shown — verbatim from original
  const trackAdViews = async (ads) => {
    if (viewsTracked) return;
    const realAds = ads.filter(ad => ad.id && typeof ad.id === 'string');
    if (realAds.length === 0) return;
    try {
      await api.post('/card-ads/track-batch', {
        card_ad_ids: realAds.map(ad => ad.id),
        session_id: sessionId,
      });
      setViewsTracked(true);
    } catch {
      // tracking failure is non-fatal
    }
  };

  // Track individual ad click — verbatim from original
  const trackAdClick = async (adId) => {
    if (!adId || typeof adId !== 'string') return;
    try {
      await api.post('/card-ads/track', {
        card_ad_id: adId,
        event_type: 'click',
        session_id: sessionId,
      });
    } catch {
      // tracking failure is non-fatal
    }
  };

  const handleFlip = () => {
    const next = !isFlipped;
    setIsFlipped(next);
    if (next) {
      const adsToShow = cardAds.length > 0 ? cardAds : DEFAULT_CARD_ADS;
      trackAdViews(adsToShow.slice(0, 2));
    }
  };

  const handleDownload = () => {
    if (!qrUrl) return;
    const link = document.createElement('a');
    link.href = qrUrl;
    link.download = `${user?.name ?? 'student'}-Library-Card.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showSnackbar('Library Card downloaded!', 'success');
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Digital Library Card',
          text: 'Check out my BookMyLibrary digital card!',
          url: window.location.href,
        });
      } catch {
        // user cancelled share
      }
    } else {
      showSnackbar('Sharing not supported on this device', 'info');
    }
  };

  const glowShadow = isLight ? SHADOWS_LIGHT.glowPrimary : SHADOWS_DARK.glowPrimary;

  const fullscreenContent = (
    <GradientSurface
      variant="brand"
      animated
      radius={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        minHeight: isMobile ? 'unset' : 480,
      }}
    >
      <MotionBox
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={reducedMotion ? { duration: 0.12 } : easing.spring.soft}
        sx={{ textAlign: 'center', width: '100%', maxWidth: 340 }}
      >
        <Avatar
          sx={{
            width: 88,
            height: 88,
            mx: 'auto',
            mb: 2,
            border: '3px solid white',
            bgcolor: 'rgba(255,255,255,0.2)',
            fontSize: '2.5rem',
          }}
        >
          {user?.avatar_url ? (
            <img
              src={user.avatar_url}
              alt={user?.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Person sx={{ fontSize: 52, color: 'white' }} />
          )}
        </Avatar>

        <Typography variant="h5" fontWeight={800} sx={{ color: 'white', mb: 0.5 }}>
          {user?.name}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mb: 3 }}>
          {activeSubscription?.library?.name ?? 'BookMyLibrary Member'}
        </Typography>

        {/* QR well */}
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: RADIUS.xl,
            p: 3,
            textAlign: 'center',
            boxShadow: glowShadow,
          }}
        >
          {qrUrl && (
            <img
              src={qrUrl}
              alt="QR Pass"
              style={{ maxWidth: '100%', width: 240, height: 'auto' }}
            />
          )}
          {user?.static_qr_id && (
            <Typography
              variant="body2"
              mt={1.5}
              color="secondary.main"
              fontWeight={700}
              sx={{ fontFamily: '"Courier New", Courier, monospace', letterSpacing: 2 }}
            >
              {user.static_qr_id}
            </Typography>
          )}
        </Box>

        <Box textAlign="center" mt={2.5}>
          <Chip
            icon={<FlashOn />}
            label="Ready to Scan"
            sx={{
              bgcolor: 'white',
              color: 'primary.main',
              fontWeight: 700,
              px: 1.5,
              py: 2.5,
              fontSize: '0.9rem',
            }}
          />
        </Box>
      </MotionBox>
    </GradientSurface>
  );

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="sm" sx={{ py: { xs: 2, md: 4 } }}>
        <MotionBox
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={reducedMotion ? { duration: 0.12 } : { duration: dur.base, ease: easing.standard }}
        >
          {/* Flip card container */}
          <Box
            role="button"
            tabIndex={0}
            aria-label="Flip library card"
            onClick={handleFlip}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleFlip();
              }
            }}
            sx={{
              perspective: '1200px',
              mb: 3,
              cursor: 'pointer',
              outline: 'none',
              maxWidth: 360,
              mx: 'auto',
              overflow: 'hidden',
              '&:focus-visible': {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 4,
                borderRadius: RADIUS.lg,
              },
            }}
          >
            <MotionBox
              animate={{ rotateY: isFlipped ? 180 : 0 }}
              transition={
                reducedMotion
                  ? { duration: 0.01 }
                  : { type: 'spring', stiffness: 180, damping: 22 }
              }
              sx={{
                position: 'relative',
                width: '100%',
                // aspect-ratio 1:1.58 (credit card proportion)
                aspectRatio: '1 / 1.58',
                transformStyle: 'preserve-3d',
                borderRadius: RADIUS.lg,
              }}
            >
              <DigitalCardFront
                name={user?.name}
                avatarUrl={user?.avatar_url}
                staticQrId={user?.static_qr_id}
                qrUrl={qrUrl}
                qrLoaded={qrLoaded}
              />
              <DigitalCardBack
                activeSubscription={activeSubscription}
                qrUrl={qrUrl}
                qrLoaded={qrLoaded}
                cardAds={cardAds.length > 0 ? cardAds : DEFAULT_CARD_ADS}
                trackAdClick={trackAdClick}
                onFullscreen={() => setShowFullscreen(true)}
              />
            </MotionBox>
          </Box>

          {/* Actions row */}
          <Box display="flex" gap={1.5} mb={2.5}>
            <Button
              variant="outlined"
              startIcon={<QrCodeScanner />}
              onClick={() => navigate('/student/scanner')}
              sx={{
                flex: 1,
                borderRadius: RADIUS.md,
                py: 1.25,
                fontWeight: 600,
                textTransform: 'none',
                borderWidth: 1,
              }}
            >
              Scan to Enter
            </Button>
            <IconButton
              onClick={handleDownload}
              aria-label="Download library card"
              sx={{
                borderRadius: RADIUS.md,
                border: `1px solid ${theme.palette.divider}`,
                width: 48,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Download />
            </IconButton>
            <IconButton
              onClick={handleShare}
              aria-label="Share library card"
              sx={{
                borderRadius: RADIUS.md,
                border: `1px solid ${theme.palette.divider}`,
                width: 48,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Share />
            </IconButton>
          </Box>

          {/* Full screen pass CTA */}
          <Button
            fullWidth
            variant="contained"
            startIcon={<FlashOn />}
            onClick={() => setShowFullscreen(true)}
            sx={{
              borderRadius: RADIUS.md,
              py: 1.25,
              fontWeight: 700,
              textTransform: 'none',
              fontSize: '1rem',
            }}
          >
            Show Full Screen Pass
          </Button>
        </MotionBox>
      </Container>

      {/* Fullscreen: BottomSheet on mobile, Dialog on desktop */}
      {isMobile ? (
        <BottomSheet
          open={showFullscreen}
          onClose={() => setShowFullscreen(false)}
          maxHeight="100vh"
          sx={{ borderTopLeftRadius: RADIUS.xl, borderTopRightRadius: RADIUS.xl }}
        >
          {fullscreenContent}
        </BottomSheet>
      ) : (
        <Dialog
          open={showFullscreen}
          onClose={() => setShowFullscreen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: RADIUS.xl, overflow: 'hidden' } }}
        >
          <DialogContent sx={{ p: 0, position: 'relative' }}>
            <IconButton
              onClick={() => setShowFullscreen(false)}
              aria-label="Close fullscreen pass"
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 2,
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(8px)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              }}
            >
              <Close />
            </IconButton>
            {fullscreenContent}
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
}

export default StudentQRPass;
